// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Alert } from '@loa/ui-components';
import {
  ASSUMPTION_FIELDS,
  type EffectiveAssumptions,
  type ProjectionAssumptions,
} from '../../api/projectionAssumptionsApi';

interface Props {
  /** This level's current override row (sparse; nulls = not set here). */
  override: ProjectionAssumptions;
  /** Resolved values + provenance, to show "inherited from {level}" hints (optional). */
  effective?: EffectiveAssumptions | null;
  onSave: (body: ProjectionAssumptions) => Promise<void>;
  /** When provided, renders a Clear (revert-to-inherited) action. */
  onClear?: () => Promise<void>;
  testId?: string;
}

/**
 * Shared editor for a single level of the projection-assumption hierarchy. Each
 * field shows this level's override; when blank, the resolved (inherited) value +
 * its source is shown as a hint. Rates are entered as percentages (the value is
 * stored as-is — 2.5 = 2.5%). Reused by the relationship gear dialog, the advisor
 * Settings page, the brokerage-admin section, and (mirrored) the Admin UI.
 */
export function ProjectionAssumptionsEditor({ override, effective, onSave, onClear, testId }: Props) {
  const [form, setForm] = useState<Record<string, string>>(() =>
    Object.fromEntries(ASSUMPTION_FIELDS.map((f) => {
      const v = override[f.key];
      return [f.key, v == null ? '' : String(v)];
    })),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const toBody = (): ProjectionAssumptions => {
    const body: Record<string, number | null> = {};
    for (const f of ASSUMPTION_FIELDS) {
      const raw = form[f.key]?.trim();
      body[f.key] = raw === undefined || raw === '' ? null : Number(raw);
    }
    return body as unknown as ProjectionAssumptions;
  };

  const save = () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    onSave(toBody())
      .then(() => setMessage('Saved.'))
      .catch(() => setError('Could not save the assumptions.'))
      .finally(() => setSaving(false));
  };

  const clear = () => {
    if (!onClear || saving) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    onClear()
      .then(() => {
        setForm(Object.fromEntries(ASSUMPTION_FIELDS.map((f) => [f.key, ''])));
        setMessage('Cleared — values re-inherit from the level above.');
      })
      .catch(() => setError('Could not clear the override.'))
      .finally(() => setSaving(false));
  };

  return (
    <div data-testid={testId ?? 'projection-assumptions-editor'} className="flex flex-col gap-[12px]">
      <div className="grid grid-cols-2 gap-[12px] md:grid-cols-3">
        {ASSUMPTION_FIELDS.map((f) => {
          const inheritedSource = effective?.sources?.[f.key];
          const inheritedValue = effective?.[f.key];
          const showInherited = !form[f.key] && inheritedSource && inheritedSource !== 'UNSET';
          return (
            <label key={f.key} className="flex flex-col gap-xs text-[13px] text-grey-04">
              <span>{f.label} <span className="text-[#8A8A8A]">({f.suffix})</span></span>
              <input
                type="number"
                data-testid={`pa-${f.key}`}
                value={form[f.key] ?? ''}
                placeholder={showInherited ? `inherited: ${inheritedValue}` : ''}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                className="rounded-lg border-[1.5px] border-[#E1E1E1] px-[10px] py-[6px] text-body-03"
              />
              {showInherited && (
                <span data-testid={`pa-${f.key}-inherited`} className="text-[11px] text-[#8A8A8A]">
                  from {inheritedSource}
                </span>
              )}
            </label>
          );
        })}
      </div>
      {error && <Alert variant="error">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}
      <div className="flex gap-[12px]">
        <button
          type="button"
          data-testid="pa-save"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-purple-primary px-md py-sm text-body-03 text-white disabled:opacity-60"
        >
          Save
        </button>
        {onClear && (
          <button
            type="button"
            data-testid="pa-clear"
            onClick={clear}
            disabled={saving}
            className="rounded-lg border-[1.5px] border-[#BDB8FF] px-md py-sm text-body-03 text-purple-primary"
          >
            Clear (inherit)
          </button>
        )}
      </div>
    </div>
  );
}
